import { useEffect, useState } from "react";
import { Alert, Button, Card, Group, Select, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";

type Grp = { chat_id: number; title: string };
type FlagKey = "free_issues.telegram_enabled" | "free_issues.act_enabled" | "free_issues.accounting_enabled";

// Implicitele din backend când cheia lipsește.
const FLAG_DEFAULTS: Record<FlagKey, boolean> = {
  "free_issues.telegram_enabled": true,
  "free_issues.act_enabled": true,
  "free_issues.accounting_enabled": false,
};
const FLAG_KEYS = Object.keys(FLAG_DEFAULTS) as FlagKey[];
const FLAG_LABEL: Record<FlagKey, string> = {
  "free_issues.telegram_enabled": "telegramEnabled",
  "free_issues.act_enabled": "actEnabled",
  "free_issues.accounting_enabled": "accountingEnabled",
};

export function FreeIssuesSettingsTab() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Grp[]>([]);
  const [chat, setChat] = useState<string>("");
  const [flags, setFlags] = useState<Record<FlagKey, boolean>>(FLAG_DEFAULTS);
  const [busy, setBusy] = useState<string | null>(null);
  // Până la o încărcare reușită nu permitem salvarea — altfel am scrie null /
  // implicite peste valorile reale.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  async function load() {
    setLoaded(false);
    setLoadError(false);
    let g: { value?: unknown };
    let c: { set: boolean; value?: unknown };
    let fl: { set: boolean; value?: unknown }[];
    try {
      [g, c, ...fl] = await Promise.all([
        api.getKnownGroups().catch(() => ({ value: [] as Grp[] })),
        api.getSetting("free_issues.telegram_chat_id"),
        ...FLAG_KEYS.map((k) => api.getSetting(k)),
      ]);
    } catch {
      setLoadError(true);
      return;
    }
    setGroups((g.value ?? []) as Grp[]);
    setChat(c.value != null ? String(c.value) : "");
    const next = { ...FLAG_DEFAULTS };
    FLAG_KEYS.forEach((k, i) => {
      const r = fl[i];
      if (r.set && r.value !== null && r.value !== undefined) next[k] = Boolean(r.value);
    });
    setFlags(next);
    setLoaded(true);
  }
  useEffect(() => { void load(); }, []);

  async function saveChat() {
    const raw = chat.trim();
    const val = raw === "" ? null : Number(raw);
    if (raw !== "" && (val === null || !Number.isInteger(val))) {
      notifications.show({ color: "red", message: t("settings.freeIssues.invalidId") });
      return;
    }
    setBusy("chat");
    try {
      await api.putSetting("free_issues.telegram_chat_id", val);
      notifications.show({ color: "green", message: t("settings.freeIssues.saved") });
    } catch {
      notifications.show({ color: "red", message: t("common.saveFailed") });
    } finally {
      setBusy(null);
    }
  }

  async function saveFlag(k: FlagKey, v: boolean) {
    const prev = flags[k];
    setFlags((f) => ({ ...f, [k]: v }));
    setBusy(k);
    try {
      await api.putSetting(k, v);
      notifications.show({ color: "green", message: t("settings.freeIssues.saved") });
    } catch {
      setFlags((f) => ({ ...f, [k]: prev }));
      notifications.show({ color: "red", message: t("common.saveFailed") });
    } finally {
      setBusy(null);
    }
  }

  const opts = groups.map((g) => ({ value: String(g.chat_id), label: `${g.title} (${g.chat_id})` }));

  return (
    <Card withBorder>
      <Stack>
        <Title order={5}>{t("settings.freeIssues.title")}</Title>
        <Text size="sm" c="dimmed">{t("settings.freeIssues.help")}</Text>
        {loadError && <Alert color="red">{t("settings.freeIssues.loadFailed")}</Alert>}
        <Group align="end" wrap="wrap">
          <Select
            label={t("settings.freeIssues.group")}
            data={opts}
            value={opts.find((o) => o.value === chat) ? chat : null}
            onChange={(v) => setChat(v ?? "")}
            searchable
            clearable
            w={280}
            placeholder={t("settings.telegramGroups.pick")}
          />
          <TextInput label={t("settings.telegramGroups.orManual")} value={chat} onChange={(e) => setChat(e.currentTarget.value)} w={160} />
          <Button loading={busy === "chat"} disabled={!loaded} onClick={() => void saveChat()}>{t("common.save")}</Button>
        </Group>
        {FLAG_KEYS.map((k) => (
          <Switch
            key={k}
            label={t(`settings.freeIssues.${FLAG_LABEL[k]}`)}
            checked={flags[k]}
            disabled={!loaded || busy === k}
            onChange={(e) => void saveFlag(k, e.currentTarget.checked)}
          />
        ))}
        <Text size="xs" c="dimmed">{t("settings.freeIssues.accountingHint")}</Text>
        <Button variant="subtle" onClick={() => void load()} w="fit-content">{t("settings.telegramGroups.reload")}</Button>
      </Stack>
    </Card>
  );
}
